import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoCloseSmall16, IcoInfo16, IcoWarning16 } from '@onefootprint/icons';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cx } from 'class-variance-authority';
import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import uniqueId from 'lodash/uniqueId';
import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../icon-button';
import ActionButton from './components/action-button';
const TOAST_WIDTH = 380;
type ToastVariant = 'default' | 'error' | 'success';
type ToastAction = {
  label: string;
  onClick?: () => void;
};
type ToastOptions = {
  title: string;
  description?: React.ReactNode;
  variant?: ToastVariant;
  primaryAction?: ToastAction;
  secondaryAction?: ToastAction;
  duration?: number;
};
type ToastProps = ToastOptions & {
  id: string;
};
type ToastFunction = (options: ToastOptions) => string;
type ToastContextType = {
  toast: ToastFunction;
  removeToast: (id: string) => void;
};
const useToast = () => {
  const $ = _c(2);
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  let t0;
  if ($[0] !== context.toast) {
    t0 = {
      toast: context.toast
    };
    $[0] = context.toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};
const ToastContext = createContext<ToastContextType>({
  toast: () => {
    console.warn('Toast provider not initialized');
    return '';
  },
  removeToast: () => console.warn('Toast provider not initialized')
});
const ToastProvider = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.toast"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [toasts, setToasts] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = options => {
      const id = uniqueId();
      setToasts(currentToasts => [...currentToasts, {
        id,
        ...options
      }]);
      return id;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const toast = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = id_0 => {
      setToasts(currentToasts_0 => currentToasts_0.filter(toast_0 => toast_0.id !== id_0));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const removeToast = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (id_1, callback) => {
      removeToast(id_1);
      callback?.();
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleAction = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      toast,
      removeToast
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t || $[7] !== toasts) {
    let t8;
    if ($[9] !== t) {
      t8 = t9 => {
        const {
          id: id_2,
          variant: t10,
          description,
          primaryAction,
          secondaryAction,
          title,
          duration: t11
        } = t9;
        const variant = t10 === undefined ? "default" : t10;
        const duration = t11 === undefined ? 3000 : t11;
        return <ToastPrimitive.Root key={id_2} duration={duration} onOpenChange={open => !open && removeToast(id_2)} forceMount={true} asChild={true}><motion.div initial={{
            opacity: 0,
            x: 100,
            scale: 0.95
          }} animate={{
            opacity: 1,
            x: 0,
            scale: 1,
            transition: {
              ease: easeInOut,
              duration: 0.15,
              delay: 0.1
            }
          }} exit={{
            opacity: 0,
            x: 100,
            scale: 0.95,
            transition: {
              ease: easeInOut,
              duration: 0.15
            }
          }} className="fixed flex flex-col items-start gap-2 p-3 rounded-default shadow-md top-4 right-4 bg-elevated max-w-[calc(100%-32px)] border border-tertiary" style={{
            width: `${TOAST_WIDTH}px`
          }}><div className="flex items-start gap-2"><div className="flex items-center justify-center flex-shrink-0 h-5">{variant === "error" && <IcoWarning16 color="error" />}{variant === "success" && <IcoCheckCircle16 color="success" />}{variant === "default" && <IcoInfo16 color="primary" />}</div><div className="flex flex-col w-full gap-1"><ToastPrimitive.Title className={cx("text-label-2", {
                  "text-error": variant === "error",
                  "text-success": variant === "success",
                  "text-primary": variant === "default"
                })}>{title}</ToastPrimitive.Title><ToastPrimitive.Description className="text-body-2 text-tertiary empty:hidden">{description}</ToastPrimitive.Description></div></div><div className="flex justify-start w-full gap-1 ml-6 empty:hidden">{primaryAction && <ActionButton altText={primaryAction.label} onClick={() => handleAction(id_2, primaryAction.onClick)}>{primaryAction.label}</ActionButton>}{secondaryAction && <><span className="text-label-2 text-tertiary">·</span><ActionButton altText={secondaryAction.label} onClick={() => handleAction(id_2, secondaryAction.onClick)}>{secondaryAction.label}</ActionButton></>}</div><ToastPrimitive.Close asChild={true} onClick={() => removeToast(id_2)}><IconButton aria-label={t("close-aria-label-default")} className="absolute top-2 right-2" size="tiny" icon={IcoCloseSmall16} color="tertiary" /></ToastPrimitive.Close></motion.div></ToastPrimitive.Root>;
      };
      $[9] = t;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    t7 = toasts.map(t8);
    $[6] = t;
    $[7] = toasts;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <AnimatePresence>{t7}</AnimatePresence>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t("viewport-label");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <ToastPrimitive.Viewport label={t9} className="fixed top-4 right-4 flex flex-col gap-2 max-w-[420px] z-50" />;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== children || $[18] !== t10 || $[19] !== t8) {
    t11 = <ToastContext.Provider value={t6}><ToastPrimitive.Provider>{children}{t8}{t10}</ToastPrimitive.Provider></ToastContext.Provider>;
    $[17] = children;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};
export type { ToastContextType, ToastFunction, ToastProps };
export { ToastProvider, useToast, useToastContext };